import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import InputMask from 'react-input-mask';
import { If } from 'react-if';
import { useDispatch, useSelector } from 'react-redux';

import Keyboard from '../../components/Keyboard/Keyboard';
import Text from '../../i18n/Text';
import Loader from '../../components/Loader/Loader';
import { storeDetails } from '../../store/actions/details';
import routes from '../../routes';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import api from '../../helpers/api';
import FullRedirect from '../../components/FullRedirect';
import { bbAnalytics } from '../../helpers/analytics/setup/analytics.service.handler';
import { useNavigation } from '../../hooks/useNavigation';
import { useConfig } from '../../hooks/Configurator';

const CheckIn = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const leave = useCallback(() => {
    bbAnalytics.triggerEventTrack('autoRefresh', null, 'CheckIn');
    navigation.replace(routes.Splash);
  }, [navigation]);

  const { CheckInPageTimeout } = useConfig(
    ({ bbGeneralOptions }) => bbGeneralOptions.timeouts
  );
  const timer = useSessionTimeout(CheckInPageTimeout * 1000, leave);

  const details = useSelector((state) => state.details);

  const inputClasses = classNames('input is-large has-text-centered', {
    'is-danger': error !== false,
  });

  const saveDetails = useCallback(
    (target) => {
      dispatch(storeDetails(target));
    },
    [dispatch]
  );

  async function onAccepted() {
    if (code.length < 8) {
      setError('CHECKIN.ERRORS.INVALID_CODE');
      bbAnalytics.triggerEventTrack(
        'checkInPage.codeError',
        'CHECKIN.ERRORS.INVALID_CODE'
      );
    } else {
      setIsRequesting(true);

      try {
        const { data: company } = await api.getCompany();
        const { timezone } = company;

        const { data: checkinData } = await api.checkin(code);
        if (!checkinData.checked_in) throw new Error();

        const { data: service } = await api.getService(checkinData);

        setIsRequesting(false);

        saveDetails({
          code,
          company,
          name: service.name,
          time: checkinData.datetime,
          timezone,
        });
      } catch (e) {
        setError('CHECKIN.ERRORS.CODE_NOT_RECOGNISED');
        bbAnalytics.triggerEventTrack(
          'checkInPage.codeError',
          'CHECKIN.ERRORS.CODE_NOT_RECOGNISED'
        );
        setIsRequesting(false);
      }
    }
  }

  function onChange(value) {
    if (error) setError(false);
    setCode(value.slice(0, 8));
  }

  useEffect(() => () => timer.reset(), [timer]);

  if (details) {
    return <FullRedirect to={routes.CheckInConfirmation} />;
  }

  return (
    <div>
      <Loader visible={isRequesting} />

      <div className="columns is-centered">
        <div className="column is-6">
          <div className="section has-text-centered">
            <h1 className="title is-3 is-spaced">
              <Text>CHECKIN.HEADING</Text>
            </h1>

            <h2 className="subtitle is-5 is-spaced">
              <Text>CHECKIN.SUBHEADING</Text>
            </h2>
          </div>
        </div>
      </div>

      <div className="columns is-centered">
        <div className="column is-3">
          <InputMask
            qa-id="check-in-input"
            mask="****-****"
            alwaysShowMask
            className={inputClasses}
            value={code}
            readOnly
            maskPlaceholder={null}
          />

          <If condition={error !== false}>
            <p
              className="help is-danger has-text-centered has-text-weight-bold"
              qa-id="error"
            >
              <Text>{error}</Text>
            </p>
          </If>

          <Keyboard onChange={onChange} onAccepted={onAccepted} code={code} />
        </div>
      </div>
    </div>
  );
};

export default CheckIn;
